import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslations } from '@omnipkg/ui-kit-web';

import { useTranslate } from '../../../hooks/useTranslate';
import { selectDeviceInfo } from '../../../store/slices/initialSlice';
import { WorkingHours } from '../../../store/types/orderResponse';
import { DEFAULT_STATUS } from '../constants';
import { DeviceWorkData, TransformedWorkingData, WorkStatus } from '../types';

export function useGetWorkingData(): DeviceWorkData {
  const [workData, setWorkData] = useState<DeviceWorkData>({
    ...DEFAULT_STATUS,
    workSchedule: [],
  });

  const { langData } = useTranslations();
  const { staticTranslate } = useTranslate();

  const WEEKLY_DAYS = {
    1: staticTranslate('monday'),
    2: staticTranslate('tuesday'),
    3: staticTranslate('wednesday'),
    4: staticTranslate('thursday'),
    5: staticTranslate('friday'),
    6: staticTranslate('saturday'),
    7: staticTranslate('sunday'),
  };

  const deviceInfo = useSelector(selectDeviceInfo);

  useEffect(() => {
    function getDeviceWorkingStatus(
      workSchedule: TransformedWorkingData[],
    ): WorkStatus {
      const today = new Date(Date.now());

      const day = today.getDay();
      const currentTime = today.toLocaleTimeString('en-GB'); // For 24h format

      const todayWorkingTime = workSchedule.find(
        ({ dayNumber }) => dayNumber === day,
      )?.time;

      if (todayWorkingTime?.length === 2) {
        const openTime = todayWorkingTime[0];
        const closeTime = todayWorkingTime[1];

        const isAvailable = currentTime >= openTime && currentTime < closeTime;

        return {
          day: WEEKLY_DAYS[day as keyof WorkingHours],
          statusValue: isAvailable
            ? staticTranslate('open')
            : staticTranslate('close'),
          isAvailable,
          closes: closeTime,
        };
      }

      return {
        ...DEFAULT_STATUS,
        statusValue: staticTranslate('noData'),
      };
    }

    const workSchedule = (function transformWorkingHours() {
      if (deviceInfo.working_hours) {
        return Object.entries(deviceInfo.working_hours).reduce<
          TransformedWorkingData[]
        >((acc, [day, time]) => {
          const workingTime = time?.[0];

          if (workingTime) {
            const weeklyDay = +day as keyof WorkingHours;
            const workingData: TransformedWorkingData = {
              dayNumber: weeklyDay,
              dayName: WEEKLY_DAYS[weeklyDay],
              time: workingTime.split('-'),
            };
            acc.push(workingData);
          }

          return acc;
        }, []);
      }
    })();

    if (workSchedule?.length) {
      setWorkData({
        ...getDeviceWorkingStatus(workSchedule),
        workSchedule,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceInfo, langData.lang]);

  return workData;
}
